import { Alert, Button } from '@global-ecom/nitro-uds/elements';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { tw } from 'twind/css';

import { EmailSignUpActions } from 'types/analyticTypes';
import {
  SignUpForm,
  useCreateContactMutation,
  Variant,
} from '__generated__/graphql';
import { useInView } from 'hooks/useInView';
import { AccordionRoot, AccordionTab } from 'ui/elements/Accordion';
import { emailSignUpEvent } from 'utils/analytics';
import { extractError } from 'utils/extractError';
import { SignUpFormDocumentType } from 'groq/documents/SignUpFormDocument';
import { useSiteConfig } from 'hooks/useSiteConfig';

import {
  createContactInput,
  SignUpFormField,
  SignupFormFields,
} from './SignUpFormField';

type SignUpFormPdpComingSoonProps = {
  signUpForm?: SignUpFormDocumentType | SignUpForm | null;
  variation?: Variant | null;
};

export const SignUpFormPdpComingSoon = ({
  signUpForm,
  variation,
}: SignUpFormPdpComingSoonProps) => {
  const [createContactResult, createContact] = useCreateContactMutation();
  const { countryCode } = useSiteConfig();
  const [signUpFormPdpComingSoonInViewRef, signUpFormPdpComingSoonInView] =
    useInView();

  useEffect(() => {
    if (signUpFormPdpComingSoonInView) {
      emailSignUpEvent(
        EmailSignUpActions.Impression,
        'SignUpFormPDPComingSoon'
      );
    }
  }, [signUpFormPdpComingSoonInView]);

  const submittedSuccessfully =
    createContactResult.data &&
    !createContactResult.error &&
    !createContactResult.fetching;

  const header = useMemo(() => {
    if (submittedSuccessfully) return signUpForm?.successHeader || '';
    return signUpForm?.header || '';
  }, [signUpForm, submittedSuccessfully]);

  const copy = useMemo(() => {
    if (submittedSuccessfully) return signUpForm?.successCopy || '';
    return signUpForm?.copy || '';
  }, [signUpForm, submittedSuccessfully]);

  const form = useForm<SignupFormFields>({
    defaultValues: {},
    shouldUnregister: false,
    mode: 'onSubmit',
  });

  const submitForm = async (data: SignupFormFields, contactSource: string) => {
    const contact = createContactInput(data, contactSource, countryCode);
    const result = await createContact({
      input: {
        contact,
      },
    });

    emailSignUpEvent(
      result.error ? EmailSignUpActions.Error : EmailSignUpActions.Submit,
      'SignUpFormPDPComingSoon'
    );

    return result;
  };

  useEffect(() => {
    if (
      form.formState.isSubmitted &&
      Object.keys(form.formState.errors).length
    ) {
      emailSignUpEvent(EmailSignUpActions.Error, 'SignUpFormPDPComingSoon');
    }
  }, [form.formState]);

  if (!signUpForm || !variation?.displayOutOfStock?.comingsoon) return null;

  const details: React.ComponentProps<typeof SignUpFormField>['details'] = {
    ctaLabel: signUpForm.submitButtonText,
  };

  return (
    <AccordionRoot>
      <AccordionTab
        bodyClassNameConfig={{
          baseClassName: 'pt-2 pb-8',
        }}
        dataTestId="sign-up-form-pdp-coming-soon-accordion"
        expand
        headerClassNameConfig={{
          baseClassName: 'font-bold text-xl h-auto',
        }}
        id="sign-up-form-pdp-coming-soon-accordion"
        title={header}
      >
        <p className="text-base mb-8">{copy}</p>
        {!submittedSuccessfully && (
          <form
            className="flex w-full flex-col"
            onSubmit={form.handleSubmit(data =>
              submitForm(data, signUpForm.contactSource)
            )}
          >
            {extractError(createContactResult) && (
              <div className="mb-4">
                <Alert
                  content={extractError(createContactResult) as string}
                  dataTestId="sign-up-form-pdp-coming-soon-error"
                  title="Error"
                  variant="error"
                />
              </div>
            )}
            <>
              <div
                className="grid gap-4 mb-8"
                ref={
                  signUpFormPdpComingSoonInViewRef as React.RefObject<HTMLDivElement>
                }
              >
                {signUpForm.fields.map(field => {
                  switch (field) {
                    case 'address': {
                      return ['address', 'city', 'zip-code'].map(name => (
                        <div key={name} className="w-full">
                          <SignUpFormField
                            form={form}
                            field={name}
                            isLoading={createContactResult.fetching}
                            details={details}
                          />
                        </div>
                      ));
                    }
                    case 'name': {
                      return ['firstName', 'lastName'].map(name => (
                        <div key={name} className="w-full">
                          <SignUpFormField
                            form={form}
                            field={name}
                            isLoading={createContactResult.fetching}
                            details={details}
                          />
                        </div>
                      ));
                    }
                    case 'ticketChoice': {
                      return null;
                    }
                    default: {
                      const fullRow = [
                        'ageCheck',
                        'emailPreference',
                        'opt-out',
                      ].includes(field);

                      return (
                        <div
                          key={field}
                          className={tw(
                            fullRow && 'flex justify-start mt-4 col-span-full'
                          )}
                        >
                          <SignUpFormField
                            form={form}
                            field={field}
                            isLoading={createContactResult.fetching}
                            details={details}
                          />
                        </div>
                      );
                    }
                  }
                })}
              </div>
              <Button
                className="w-full"
                dataTestId="sign-up-form-pdp-coming-soon-submit-button"
                label={signUpForm.submitButtonText}
                size="lg"
                type="submit"
              />
            </>
          </form>
        )}
      </AccordionTab>
    </AccordionRoot>
  );
};
